import { SELLER_UNITS_AND_PROJECTS_QUERY } from "@/gql/sup";
import { type SellerUnitAndProject, type SellerUnitsAndProjectsOne } from "@/types/sup";
import { logger } from "@/utils/logger";
import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

export interface UseSupOneResult {
	data: SellerUnitsAndProjectsOne[];
	loading: boolean;
	initialLoading: boolean;
	error?: Error;
}

export function useSupOne(ids: string[], initialData?: SellerUnitAndProject[]): UseSupOneResult {
	// If we have initial data, start with initialLoading as false
	const [initialLoading, setInitialLoading] = useState(!initialData || initialData.length === 0);

	const {
		data: supOne,
		loading,
		error: apolloError,
	} = useQuery(SELLER_UNITS_AND_PROJECTS_QUERY, {
		variables: {
			params: {
				details: {
					ids: ids,
				},
			},
		},
		fetchPolicy: "cache-first",
		skip: !ids || ids.length === 0,
	});

	if (apolloError) {
		const graphQLErrors = apolloError.graphQLErrors || [];
		graphQLErrors.forEach(gqlError => {
			if (gqlError.path && Array.isArray(gqlError.path)) {
				const detailIndex = gqlError.path[2];
				if (typeof detailIndex === "number" && ids[detailIndex]) {
					const problematicId = ids[detailIndex];
					logger.error(`GraphQL Error for property ID ${problematicId} (index ${detailIndex}):`, {
						message: gqlError.message,
						path: gqlError.path,
						propertyId: problematicId,
						allIds: ids,
					});
				}
			}
		});
	}

	useEffect(() => {
		// Once data is received or an error occurs, the initial loading is complete.
		if (!loading || (initialData && initialData.length > 0)) {
			setInitialLoading(false);
		}
	}, [loading, initialData]);

	// Return raw data from GraphQL, with initialData fallback while loading
	const rawData = supOne?.sellerUnitsAndProjects?.details || [];

	// Use initial data immediately if available, otherwise use fetched data
	const resultData: SellerUnitsAndProjectsOne[] =
		rawData.length > 0 ? rawData : loading && initialData ? initialData : rawData;

	const sortedSups = [];
	if (resultData.length > 1) {
		for (const id of ids) {
			const item = resultData.find(sup => sup._id === id);
			if (item) sortedSups.push(item);
		}
	} else if (resultData.length) sortedSups.push(resultData[0]);

	return {
		data: sortedSups,
		loading: rawData.length === 0 && loading, // Only show loading if we don't have fresh data
		initialLoading,
		error: apolloError,
	};
}
